// @ts-check

/**
 * @file Renders comments and a composer inside a task detail modal.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useMemo} from "react"
import {Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import useLiveQuery from "velocious/build/src/database/use-live-query.js"
import Button from "../button"
import FormTextInput from "../form/form-text-input"
import {Comment} from "../../frontend-models/comment"
import errorMessage from "../../shared/error-message"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import TaskThreadComment from "./thread-comment"
import LocalComment from "../../models/comment"
import LocalUser from "../../models/user"
import {projectReplicaRowsSource} from "../../database/project-replica-reads"
import {queueOfflineCommentCreate} from "../../database/project-replica-comment-commands"
import {useActiveProjectReplica} from "../../sync/project-sync-lifecycle"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TaskModalCommentsProps
 * @property {boolean} [online] Whether online-only comment creation is available.
 * @property {() => Promise<void>} [onCreated] Callback after a comment is created.
 * @property {string} projectId Owning project replica id.
 * @property {{id: () => string | number}} task Task whose comments are rendered and created.
 * @property {string} testID Base test id for rendered elements.
 */

/** @typedef {{commentFormVersion: number, status: {kind: "error", text: string} | null, saving: boolean}} TaskModalCommentsState */

/**
 * Renders comments and an inline composer for a task.
 * @augments {ShapeComponent<TaskModalCommentsProps, TaskModalCommentsState>}
 */
class TaskModalComments extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  commentFormObjectRef = createRef()
  /** @type {TaskModalCommentsState} */
  state = {
    commentFormVersion: 0,
    saving: false,
    status: null
  }

  /** @type {TaskCommentReadView[]} */
  comments = []
  loading = true
  readError = /** @type {Error | null} */ (null)
  /** Active project replica, present when the offline queue can accept a comment. */
  replica = null

  /** Initializes locale and theme handling. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const projectId = this.p.projectId
    const taskId = String(this.p.task.id())
    const replica = useActiveProjectReplica(projectId)
    this.replica = replica
    const commentsSource = useMemo(() => replica && projectReplicaRowsSource({
      modelClass: LocalComment,
      projectId,
      query: (comments) => comments
        .ransack({resourceIdEq: taskId, resourceTypeEq: "Task"})
        .order({column: "created_at", direction: "DESC"})
        .order({column: "id", direction: "DESC"}),
      replica
    }), [projectId, replica, taskId])
    const commentsState = useLiveQuery(commentsSource)
    const userIdsKey = [...new Set(commentsState.results.map((comment) => comment.userId()).filter(Boolean))].sort().join(",")
    const usersQuery = useMemo(() => userIdsKey ? LocalUser.where({id: userIdsKey.split(",")}) : LocalUser.where({id: []}), [userIdsKey])
    const usersState = useLiveQuery(usersQuery)
    const usersById = Object.fromEntries(usersState.results.map((user) => [user.id(), user]))

    this.comments = commentsState.results.map((comment) => new TaskCommentReadView({
      comment,
      user: usersById[String(comment.userId())] || null
    }))
    this.loading = !replica || commentsState.loading || usersState.loading
    this.readError = commentsState.error || usersState.error || null
  }

  /**
   * Renders the modal comments section.
   * @returns {ReactNode} Modal comments section.
   */
  render() {
    const {colors, themeName} = this.tt
    const {commentFormVersion, saving, status} = this.s
    const {testID} = this.p
    const comments = this.comments
    const readStatus = status?.text || this.readError?.message || null

    return (
      <View
        style={styles[`section-${themeName}`] ||= {
          borderTopColor: colors.divider,
          borderTopWidth: 1,
          marginTop: 20,
          paddingTop: 16
        }}
        testID={`${testID}/commentsSection`}
      >
        <Text
          style={styles[`title-${themeName}`] ||= {
            color: colors.text,
            fontSize: 18,
            fontWeight: 700,
            marginBottom: 12
          }}
          testID={`${testID}/commentsSection/title`}
        >
          {_("Comments")}
        </Text>
        {this.loading && comments.length === 0 &&
          <Text
            style={styles[`empty-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 14,
              marginBottom: 12
            }}
            testID={`${testID}/commentsSection/loading`}
          >
            {_("Loading...")}
          </Text>
        }
        {!this.loading && !readStatus && comments.length === 0 &&
          <Text
            style={styles[`empty-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 14,
              marginBottom: 12
            }}
            testID={`${testID}/commentsSection/empty`}
          >
            {_("No comments found")}
          </Text>
        }
        {comments.map((comment) => renderComment({colors, comment, testID, themeName}))}
        {readStatus &&
          <Text
            style={styles[`status-${themeName}`] ||= {
              color: colors.dangerText,
              fontSize: 14,
              marginTop: 8
            }}
            testID={`${testID}/commentStatus`}
          >
            {readStatus}
          </Text>
        }
        {(this.p.online || Boolean(this.replica)) &&
          <Form
            formObjectRef={this.commentFormObjectRef}
            key={commentFormVersion}
            onSubmit={this.tt.onSubmit}
          >
            <FormTextInput
              label={_("Add a comment")}
              multiline
              name="comment"
              testID={`${testID}/newCommentInput`}
            />
            <View style={styles.submitButton ||= {marginTop: 12}}>
              <Button
                disabled={saving}
                iconName="paper-plane"
                label={saving ? _("Saving...") : _("Add comment")}
                onPress={this.tt.onSubmit}
                testID={`${testID}/submitCommentButton`}
              />
            </View>
          </Form>
        }
      </View>
    )
  }

  /** Creates a comment for the current task, online or queued offline. */
  onSubmit = async () => {
    if (this.p.online) {
      await this.submitOnline()
      return
    }

    // Offline: queue the append-only comment against the local replica. The
    // compound comment+state-change intent is wired through the task edit
    // surface (which owns the selected state); the modal composer is
    // comment-only by design.
    if (!this.replica) return

    const formObject = this.commentFormObjectRef.current
    if (!formObject || this.s.saving) return

    const commentText = String(formObject.asObject().comment || "").trim()
    if (!commentText) return

    this.s.saving = true
    this.s.status = null

    try {
      await queueOfflineCommentCreate({
        comment: commentText,
        replica: this.replica,
        taskId: String(this.p.task.id())
      })
      this.s.commentFormVersion += 1
      await this.p.onCreated?.()
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    } finally {
      this.s.saving = false
    }
  }

  /** Creates a comment for the current task through the online transport. */
  submitOnline = async () => {
    const formObject = this.commentFormObjectRef.current
    if (!formObject || this.s.saving) return

    const commentText = String(formObject.asObject().comment || "").trim()
    if (!commentText) return

    this.s.saving = true
    this.s.status = null
    try {
      await Comment.create({
        comment: commentText,
        resourceId: String(this.p.task.id()),
        resourceType: "Task"
      })
      this.s.commentFormVersion += 1
      await this.p.onCreated?.()
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    } finally {
      this.s.saving = false
    }
  }
}

/** @type {NamedExoticComponent<TaskModalCommentsProps>} */
const TaskModalCommentsComponent = memo(shapeComponent(TaskModalComments))

export default TaskModalCommentsComponent

/** Immutable local comment plus its catalog author. */
class TaskCommentReadView {
  /**
   * Captures locally synchronized display records.
   * @param {{comment: InstanceType<typeof LocalComment>, user: InstanceType<typeof LocalUser> | null}} args Local records.
   */
  constructor({comment, user}) {
    this.commentRecord = comment
    this.userRecord = user
  }

  /**
   * Reads the automation classification used to style the entry.
   * @returns {string | null} Classification synchronized from the task thread.
   */
  automationKind() { return this.commentRecord.automationKind() }

  /**
   * Reads the rendered comment body.
   * @returns {string | null} Synchronized HTML content.
   */
  comment() { return this.commentRecord.comment() }

  /**
   * Reads the timestamp used for deterministic thread ordering.
   * @returns {Date} Synchronized creation time.
   */
  createdAt() { return this.commentRecord.createdAt() }

  /**
   * Reads the identity used for React keys.
   * @returns {string} Durable comment identifier.
   */
  id() { return this.commentRecord.id() }

  /**
   * Reads the separately joined catalog author.
   * @returns {InstanceType<typeof LocalUser> | null} Matching author when available.
   */
  user() { return this.userRecord }
}

/**
 * Renders a comment card in a task detail modal.
 * @param {{colors: ReturnType<typeof useThemeColors>, comment: TaskCommentReadView, testID: string, themeName: string}} args Render inputs.
 * @returns {ReactNode} Comment card.
 */
function renderComment(args) {
  const {comment, testID} = args
  const commentID = String(comment.id())

  return (
    <TaskThreadComment
      comment={comment}
      compact
      key={commentID}
      testID={`${testID}/comment-${commentID}`}
      unknownAuthorLabel={_("Unknown user")}
    />
  )
}
